import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';

/**
 * In-app confirm dialog for Playing Platform (replaces window.confirm on mobile).
 */
export default function PlayingConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  busy = false,
  danger = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="playing-confirm-title"
      onClick={() => { if (!busy) onCancel?.(); }}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="playing-confirm-title" className="text-lg font-semibold text-slate-900">
          {title || t('playing.confirmTitle', { defaultValue: 'Please confirm' })}
        </h2>
        {message && (
          <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{message}</p>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="page-secondary" disabled={busy} onClick={onCancel}>
            {cancelLabel || t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={danger ? 'page-secondary' : 'page-primary'}
            className={danger ? 'border-rose-300 text-rose-700 hover:bg-rose-50' : undefined}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel || t('common.confirm', { defaultValue: 'Confirm' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Hook: requestConfirm(message) => Promise<boolean> */
export function usePlayingConfirm() {
  const [state, setState] = React.useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    danger: false,
    resolve: null,
  });

  const requestConfirm = React.useCallback((opts) => {
    const options = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => {
      setState({
        open: true,
        title: options.title || '',
        message: options.message || '',
        confirmLabel: options.confirmLabel || '',
        danger: !!options.danger,
        resolve,
      });
    });
  }, []);

  const close = (ok) => {
    setState((prev) => {
      prev.resolve?.(ok);
      return { ...prev, open: false, resolve: null };
    });
  };

  const dialog = (
    <PlayingConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      danger={state.danger}
      onCancel={() => close(false)}
      onConfirm={() => close(true)}
    />
  );

  return { requestConfirm, dialog };
}
